// src/hooks/useCaDefaultDeductible.ts
import { useCallback, useRef } from "react";
import { useStatsigClient, type PrecomputedEvaluationsInterface, type StatsigClientEvent } from "@statsig/react-bindings";
import { PublicConfig } from "../PublicConfig";
import { Quote } from "../types/Quote.interface";
import { AnalyticsUtils } from "../utils/AnalyticsUtils";
import { StateUtils } from "../utils/StateUtils";
import { CA_DEFAULT_DEDUCTIBLE_TEST, CaDefaultDeductibleUtils, NewQuoteAudienceParams } from "../utils/CaDefaultDeductibleUtils";
import { logLayerExposure } from "./useStatsig";

const LAYER_ID = PublicConfig.STATSIG_LAYER_ID_MAIN;
const LAYER_PARAMETERS = [{ key: CA_DEFAULT_DEDUCTIBLE_TEST.param, defaultValue: false }] as const;
/**
 * How long a submit waits for the `ratingState` attribute to be reflected in the client's evaluation.
 * Bounded so the Statsig proxy can never hold a quote write hostage: on timeout the current evaluation
 * stands (layer default: $500, no exposure, the quoter is simply not in the test for this quote).
 */
const RATING_STATE_SYNC_TIMEOUT_MS = 1500;
/** The states whose `ratingState` must reach Statsig before the first quote write: the gate targets them. */
const PRE_QUOTE_RATING_STATES: readonly string[] = [CA_DEFAULT_DEDUCTIBLE_TEST.state];

export type ResolveNewQuotePolicies = (params: NewQuoteAudienceParams) => Promise<Quote["policies"]>;

/** Resolves when the client's in-flight update settles (status leaves "Loading"), or after `timeoutMs`. */
const waitForInFlightUpdate = (client: PrecomputedEvaluationsInterface, timeoutMs: number) =>
    new Promise<void>(resolve => {
        const settle = () => {
            clearTimeout(timer);
            client.off("values_updated", onValuesUpdated);
            resolve();
        };
        const onValuesUpdated = ({ status }: StatsigClientEvent<"values_updated">) => {
            if (status !== "Loading") settle();
        };
        const timer = setTimeout(settle, timeoutMs);
        client.on("values_updated", onValuesUpdated);
    });

/**
 * Resolves once the client evaluates with `custom.ratingState === ratingState`, or after the timeout.
 *
 * `useStatsigUserUpdate` pushes the attribute as soon as the typed zip resolves, so by submit it has
 * usually landed. Two gaps remain for a fast quoter (autofill, Continue within a second): the push has
 * not been made yet, or its fetch is still in flight (`updateUserAsync` installs the user and sets
 * `loadingStatus` to "Loading" synchronously, but the gate only re-evaluates once the network values
 * land). Push only when missing — a second `updateUserAsync` for the same user would discard the
 * in-flight response ("User changed during update") — then wait on the update, not the request.
 *
 * Not the SDK's `timeoutMs`: on its timeout a late response goes to the cache only, so the client would
 * keep serving the pre-push evaluation while reporting the attribute as current, and a resubmit after a
 * failed create would read that stale evaluation again. Unbounded, the late response lands in the store.
 */
async function ensureRatingStateEvaluated(client: PrecomputedEvaluationsInterface, ratingState: string): Promise<void> {
    const user = client.getContext().user;
    if (user?.custom?.ratingState !== ratingState) {
        // The SDK never rejects here (its error boundary swallows throws; it returns null on a synchronous one),
        // so a failure is only visible on the details. Not logged while "Loading" (superseded by a newer update,
        // which carries the attribute) or "Uninitialized" (the no-op client: Statsig disabled, every call fails).
        // Either way the submit fails open to whatever the client evaluates to.
        void Promise.resolve(client.updateUserAsync({ ...user, custom: { ...user?.custom, ratingState } })).then(details => {
            if (details && !details.success && client.loadingStatus === "Ready") {
                console.error(`Failed to sync the Statsig ratingState before the quote write: ${details.error}`);
            }
        });
    }
    if (client.loadingStatus === "Loading") await waitForInFlightUpdate(client, RATING_STATE_SYNC_TIMEOUT_MS);
}

/**
 * ENG-20299 — "$750 deductible in California" test (see CaDefaultDeductibleUtils for the audience).
 *
 * `resolveNewQuotePolicies` is for the pets-step submit of a NEW quote: for a quote in the audience it
 * makes sure the client's evaluation carries the quoter's state (bounded wait), decides the treatment off
 * that evaluation, logs the exposure (both arms) from the same evaluation in the same tick, and returns
 * the policies to persist. That write is the ONLY place the flag is read. Nothing is logged at render: a
 * quoter who never creates a quote never sees a different price, so they are not counted. Not
 * `useStatsig`: its exposure is an effect that re-runs when the Statsig user updates, which could count a
 * quoter under an arm they never got; the imperative read keeps decision and exposure together.
 *
 * The exposure is logged once per mount: a create that fails and is resubmitted (API error, the Sam's
 * authorization round trip, a deprecated-pcode retry) is the same quoter, not a second participant.
 *
 * Deliberately no render-time read for the coverage step: the treatment lives on the quote's policies,
 * and a pet added later copies the first pet's coverage while the pets are linked (the default). Keying
 * the step's default plan to the live flag would hand uncounted $750 defaults to quotes that were never
 * in the test — returning and API-created CA quotes, or a quoter whose attribute reached Statsig only
 * after they submitted.
 */
export const useCaDefaultDeductible = (): { resolveNewQuotePolicies: ResolveNewQuotePolicies; preQuoteRatingStates: readonly string[] } => {
    const { client } = useStatsigClient();
    const exposureLoggedRef = useRef(false);

    const resolveNewQuotePolicies = useCallback<ResolveNewQuotePolicies>(
        async params => {
            if (!CaDefaultDeductibleUtils.isEligibleNewQuote(params)) return params.policies;

            // Eligible means the state normalized to the target state; evaluate under exactly that value.
            await ensureRatingStateEvaluated(client, StateUtils.normalizeStateCode(params.ratingState) ?? CA_DEFAULT_DEDUCTIBLE_TEST.state);

            const enabled = client.getLayer(LAYER_ID, { disableExposureLog: true }).get(CA_DEFAULT_DEDUCTIBLE_TEST.param, false) === true;
            if (!exposureLoggedRef.current) {
                exposureLoggedRef.current = logLayerExposure({ client, layerId: LAYER_ID, layerParameters: LAYER_PARAMETERS, queryParams: AnalyticsUtils.getQueryParams() });
            }
            return enabled ? CaDefaultDeductibleUtils.applyToPolicies(params.policies) : params.policies;
        },
        [client]
    );

    return { resolveNewQuotePolicies, preQuoteRatingStates: PRE_QUOTE_RATING_STATES };
};
